import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import a, { AxiosResponse, AxiosError } from 'axios';
import camelcaseKeys from 'camelcase-keys';
import dayjs from 'dayjs';
import { getHamptonSort, setHamptonSort } from 'helpers/localStorageHelpers';
import GaEventManager from 'services/ga/gaEventManager';
import { buyerActions } from 'state/buyer.slice';

const axios = a.create({
  baseURL: process.env.REACT_APP_BUYERS_BE,
  withCredentials: true,
});

type HamptonState = {
  auctions: Record<number, HamptonTypes.HamptonStateAuction>;
  auctionsBids: Record<number, HamptonTypes.HamptonStateBids>;
  filters: HamptonTypes.Filters;
  personalizedFilters: HamptonTypes.PersonalizedFilters;
  sort: HamptonTypes.Sort;
  mediaModal: {
    isOpen: boolean;
    activeItem: string;
    items: string[];
  };
  liveAuctionId: number;
  selectedAuctionId: number;
  selectedLotId: number | null;
  viewedLots: Record<number, boolean>;
  watchingLots: Record<number, boolean>;
};

export const initialAuction: HamptonTypes.HamptonStateAuction = {
  isLoading: false,
  promise: 'idle',
  data: null,
  termsAccepted: false,
};

export const initialBid: HamptonTypes.HamptonStateBids = {
  isLoading: false,
  isSaving: false,
  promise: 'idle',
  errorMessage: undefined,
  data: null,
};

const initialState: HamptonState = {
  auctions: {},
  auctionsBids: {},
  filters: {
    search: '',
    type: [0],
    description: '',
  },
  personalizedFilters: {
    notViewed: false,
    watching: false,
  },
  sort: getHamptonSort(),
  mediaModal: {
    isOpen: false,
    activeItem: '',
    items: [],
  },
  liveAuctionId: 0,
  selectedAuctionId: 0,
  selectedLotId: null,
  viewedLots: {},
  watchingLots: {},
};

export const getHampton = createAsyncThunk(
  'hampton/getHampton',
  async (payload: { hamptonAuctionId?: number }, { rejectWithValue, getState, dispatch }) => {
    const { hamptonAuctionId } = payload;
    try {
      const { data } = await axios.get<HamptonTypes.GetHamptonResponse>(
        `/hampton${hamptonAuctionId ? `/${hamptonAuctionId}` : ''}`,
      );

      dispatch(getHamptonBids({ auctionId: data.id }));
      dispatch(getAcceptedTerms({ auctionId: data.id }));
      return data;
    } catch (error) {
      window.rollbar?.error('Hampton', 'Failed to get hampton');
      return rejectWithValue('Failed to get hampton');
    }
  },
);

export const getHamptonBids = createAsyncThunk(
  'hampton/getHamptonBids',
  async (payload: { auctionId: number }, { rejectWithValue }) => {
    try {
      const { auctionId } = payload;
      const { data } = await axios.get<HamptonTypes.GetHamptonBidsResponse>(
        `/hampton/${auctionId}/hamptonBids`,
      );
      return { auctionId, data };
    } catch (error) {
      window.rollbar?.error('Hampton', 'Failed to get bids');
      return rejectWithValue('Failed to get bids');
    }
  },
);

export const getAcceptedTerms = createAsyncThunk(
  'hampton/getAcceptedTerms',
  async (payload: { auctionId: number }, { rejectWithValue }) => {
    try {
      const { auctionId } = payload;
      const { data } = await axios.get<{ acceptedTerms: boolean }>(
        `/hampton/${auctionId}/acceptHamptonTerms`,
      );
      return { auctionId, data };
    } catch (error) {
      window.rollbar?.error('Hampton', 'Failed to get terms accepted');
      return rejectWithValue('Failed to get terms');
    }
  },
);

export const updateHamptonBid = createAsyncThunk(
  'hampton/updateHamptonBid',
  async (
    payload: { auctionId: number; lotId: number; bid: number; prevBid?: number | null },
    { rejectWithValue },
  ) => {
    const { auctionId, lotId, bid, prevBid } = payload;
    try {
      // we send event before bid is placed to catch cases where API fails
      if (!!prevBid) {
        GaEventManager.GA4SendActionSubmit({
          actionedObject: 'updateBid',
          actionedValue: String(Math.round(bid)),
          actionedObject2: 'previousBid',
          actionedValue2: String(Math.round(prevBid)),
          fieldName: 'hamptonAuctionAndLotId',
          fieldValue: `${auctionId}-${lotId}`,
        });
      } else if (bid === 0) {
        GaEventManager.GA4SendActionSubmit({
          actionedObject: 'removeBid',
          fieldName: 'hamptonAuctionAndLotId',
          fieldValue: `${auctionId}-${lotId}`,
        });
      } else {
        GaEventManager.GA4SendActionSubmit({
          actionedObject: 'placeBid',
          actionedValue: String(Math.round(bid)),
          fieldName: 'hamptonAuctionAndLotId',
          fieldValue: `${auctionId}-${lotId}`,
        });
      }

      await axios.post<
        { bidAmount: number; acceptTermsAndConditions: boolean },
        AxiosResponse<{ message: string }>
      >(`/hampton/${auctionId}/lot/${lotId}/setHamptonBid `, {
        bidAmount: bid,
        acceptTermsAndConditions: true,
      });
      return { auctionId, lotId, bid };
    } catch (e) {
      const error = e as Error | AxiosError<{ code?: string; message?: string }>;

      if (a.isAxiosError(error)) {
        const response = error.response as AxiosResponse<{ code?: string; message?: string }>;
        if (response?.data.code === 'DUPLICATE_BID') {
          return rejectWithValue('Bid amount already taken, select another value');
        }
        if (response?.data.code === 'PRE_BID_PERIOD_OVER') {
          setTimeout(() => {
            window.location.reload();
          }, 1000);
          return rejectWithValue('Pre-bid period has ended over');
        }
      }

      return rejectWithValue('Failed to update bid');
    }
  },
);

export const hamptonSlice = createSlice({
  name: 'hampton',
  initialState,
  reducers: {
    setSelectedHamptonAuctionId: (state, action: PayloadAction<{ hamptonId: number }>) => {
      state.selectedAuctionId = action.payload.hamptonId;
    },
    setSelectedLotId: (state, action: PayloadAction<{ lotId: number }>) => {
      state.selectedLotId = action.payload.lotId;
    },
    clearBidError: (state) => {
      state.auctionsBids[state.selectedAuctionId].errorMessage = undefined;
    },
    setSearch: (state, action: PayloadAction<{ search: string }>) => {
      state.filters.search = action.payload.search;
    },
    setDescription: (state, action: PayloadAction<{ description: string }>) => {
      state.filters.description = action.payload.description;
    },
    setType: (state, action: PayloadAction<{ type: string[] }>) => {
      state.filters.type = action.payload.type;
    },
    setFilters: (state, action: PayloadAction<HamptonState['filters']>) => {
      state.filters = action.payload;
    },
    onFilterChange: (state, action: PayloadAction<Partial<HamptonState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    onPersonalizedFilterChange: (
      state,
      action: PayloadAction<Partial<HamptonState['personalizedFilters']>>,
    ) => {
      state.personalizedFilters = { ...state.personalizedFilters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
      state.personalizedFilters = initialState.personalizedFilters;
    },
    setSort: (state, action: PayloadAction<HamptonTypes.Sort>) => {
      setHamptonSort(action.payload);
      state.sort = action.payload;
    },
    openMediaModal: (state, action: PayloadAction<{ items: string[]; activeItem: string }>) => {
      state.mediaModal.isOpen = true;
      state.mediaModal.activeItem = action.payload.activeItem;
      state.mediaModal.items = action.payload.items;
    },
    closeMediaModal: (state) => {
      state.mediaModal.isOpen = false;
      state.mediaModal.activeItem = '';
      state.mediaModal.items = [];
    },
    viewLot: (state, action: PayloadAction<{ lotId: number }>) => {
      if (state.liveAuctionId === state.selectedAuctionId) {
        state.viewedLots[action.payload.lotId] = true;
      }
    },
    watchLot: (state, action: PayloadAction<{ lotId: number }>) => {
      state.watchingLots[action.payload.lotId] = true;
    },
    unwatchLot: (state, action: PayloadAction<{ lotId: number }>) => {
      state.watchingLots[action.payload.lotId] = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getHampton.pending, (state, action) => {
      const { meta } = action;
      const auctionId = meta.arg.hamptonAuctionId || state.selectedAuctionId;

      const auction: HamptonTypes.HamptonStateAuction = {
        ...initialAuction,
        ...state.auctions[auctionId],
        isLoading: true,
        promise: 'pending',
      };

      state.auctions[auctionId] = auction;
    });
    builder.addCase(
      getHampton.fulfilled,
      (state, action: PayloadAction<HamptonTypes.GetHamptonResponse>) => {
        const { payload } = action;

        const auction: HamptonTypes.HamptonStateAuction = {
          ...initialAuction,
          ...state.auctions[payload.id],
          isLoading: false,
          promise: 'fulfilled',
          data: {
            lots: camelcaseKeys(action.payload.lots).map((lot) => ({
              ...lot,
              parentAuctionId: action.payload.id,
            })),
            lotsMap: camelcaseKeys(action.payload.lots).reduce(
              (acc, lot) => {
                return { ...acc, [lot.lotNumber]: { ...lot, parentAuctionId: action.payload.id } };
              },
              {} as { number: HamptonTypes.Lot },
            ),
            hamptonId: action.payload.id,
            preBidsEndTime: action.payload.preBidsEndTime,
            startTime: action.payload.startTime,
            endTime: action.payload.endTime,
            previewStartAt: action.payload.previewStartAt,
          },
          termsAccepted: false,
        };

        state.auctions[payload.id] = auction;
        state.selectedAuctionId = payload.id;
        if (dayjs().isBefore(dayjs(payload.endTime))) {
          state.liveAuctionId = payload.id;
        }
      },
    );
    builder.addCase(getHampton.rejected, (state, action) => {
      const { meta } = action;
      const auctionId = meta.arg.hamptonAuctionId || state.selectedAuctionId;

      const auction: HamptonTypes.HamptonStateAuction = {
        ...initialAuction,
        ...state.auctions[auctionId],
        isLoading: false,
        promise: 'rejected',
      };

      state.auctions[auctionId] = auction;
    });
    builder.addCase(getHamptonBids.pending, (state, action) => {
      const { auctionId } = action.meta.arg;

      const bids: HamptonTypes.HamptonStateBids = {
        ...initialBid,
        ...state.auctionsBids[auctionId],
        isLoading: true,
        promise: 'pending',
      };

      state.auctionsBids[auctionId] = bids;
    });
    builder.addCase(
      getHamptonBids.fulfilled,
      (
        state,
        action: PayloadAction<{ auctionId: number; data: HamptonTypes.GetHamptonBidsResponse }>,
      ) => {
        const { auctionId, data } = action.payload;

        const bidsMap = data.reduce(
          (acc, bid) => {
            return { ...acc, [bid.lotNumber]: { yourBid: bid.lastBidAmount } };
          },
          {} as { number: number },
        );
        const bids: HamptonTypes.HamptonStateBids = {
          ...initialBid,
          ...state.auctionsBids[auctionId],
          isLoading: false,
          promise: 'fulfilled',
          data: {
            bids: data,
            bidsMap,
          },
        };

        state.auctionsBids[auctionId] = bids;
      },
    );
    builder.addCase(getHamptonBids.rejected, (state, action) => {
      const { auctionId } = action.meta.arg;

      const bids: HamptonTypes.HamptonStateBids = {
        ...initialBid,
        ...state.auctionsBids[auctionId],
        isLoading: false,
        promise: 'rejected',
      };

      state.auctionsBids[auctionId] = bids;
    });
    builder.addCase(updateHamptonBid.pending, (state, action) => {
      const { auctionId } = action.meta.arg;

      state.auctionsBids[auctionId].isSaving = true;
      state.auctionsBids[auctionId].errorMessage = undefined;
    });
    builder.addCase(
      updateHamptonBid.fulfilled,
      (state, action: PayloadAction<{ auctionId: number; lotId: number; bid: number }>) => {
        const { payload } = action;

        state.auctionsBids[payload.auctionId].data!.bidsMap[payload.lotId] = {
          yourBid: payload.bid,
        };
        state.auctionsBids[payload.auctionId].isSaving = false;
        state.auctions[payload.auctionId].termsAccepted = true;
      },
    );
    builder.addCase(updateHamptonBid.rejected, (state, action) => {
      const { payload, meta } = action;
      const { auctionId } = meta.arg;

      state.auctionsBids[auctionId].isSaving = false;
      if (meta.arg.bid !== 0) {
        state.auctionsBids[auctionId].errorMessage = payload as string;
      }
    });
    builder.addCase(getAcceptedTerms.fulfilled, (state, action) => {
      const { payload } = action;

      state.auctions[payload.auctionId].termsAccepted = payload.data.acceptedTerms;
    });
    builder.addCase(buyerActions.fetchPersonalizedAuctions.fulfilled, (state, action) => {
      state.viewedLots = {
        ...state.viewedLots,
        ...action.payload.viewedHamptonLots?.reduce(
          (acc, lotId) => {
            return { ...acc, [lotId]: true };
          },
          {} as Record<number, boolean>,
        ),
      };
      state.watchingLots = {
        ...state.watchingLots,
        ...action.payload.watchingHamptonLots?.reduce(
          (acc, lotId) => {
            return { ...acc, [lotId]: true };
          },
          {} as Record<number, boolean>,
        ),
      };
    });
  },
});

export const hamptonActions = {
  ...hamptonSlice.actions,
  getHampton,
  getHamptonBids,
  updateHamptonBid,
};

export default hamptonSlice.reducer;
